import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import CrossReferenceService from '@/services/CatalogServices/CrossReferenceService';
import { extractError } from '@/helpers/extractApiError';

const model = {
    data: null,
    submitting: false,
    loading: false,
    errors: null,
    successOnCreate: '',
    productCrossReferenceListOptions: [],
};

export const createCrossReference = createAsyncThunk(
    'cross-reference/createCrossReference',
    async (data, thunkApi) => {
        try {
            const { product_cross_reference_id: crossReferenceId } = data;
            let response;
            if (crossReferenceId) {
                response = await CrossReferenceService.editCrossReference(crossReferenceId, data);
            } else {
                response = await CrossReferenceService.createCrossReference(data);
            }
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

export const viewCrossReference = createAsyncThunk(
    'cross-reference/viewCrossReference',
    async (crossReferenceId, thunkApi) => {
        try {
            const response = await CrossReferenceService.viewCrossReference(crossReferenceId);
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

export const getProductCrossReferenceListOptions = createAsyncThunk(
    'cross-reference/getProductCrossReferenceListOptions',
    async (data, thunkApi) => {
        try {
            const response = await CrossReferenceService.getProductCrossReferenceListOptions(data);
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

const createCrossReferenceSlice = createSlice({
    name: 'createCrossReference',
    initialState: model,
    reducers: {
        resetCrossReferenceState: (state) => {
            state.data = null;
            state.submitting = false;
            state.errors = null;
        },
        resetCrossReferenceStateSuccess: (state) => {
            state.successOnCreate = '';
        },
        resetCrossReferenceStateError: (state) => {
            state.errors = null;
        },
    },
    extraReducers: (builder) => {
        // create cross reference
        builder.addCase(createCrossReference.pending, (state) => {
            state.submitting = true;
        }).addCase(createCrossReference.fulfilled, (state, action) => {
            state.submitting = false;
            state.data = action.payload;
            state.successOnCreate = action.meta.arg.product_cross_reference_id ? 'Cross reference updated successfully!' : 'Cross reference created successfully!';
        }).addCase(createCrossReference.rejected, (state, action) => {
            state.submitting = false;
            state.errors = action.payload;
        });

        // view cross reference for edit
        builder.addCase(viewCrossReference.pending, (state) => {
            state.loading = true;
        }).addCase(viewCrossReference.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload;
            state.productCrossReferenceListOptions = [
                ...state.productCrossReferenceListOptions,
                {
                    id: action.payload.product_id,
                    name: action.payload.product_name,
                },
            ];
        }).addCase(viewCrossReference.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // get product cross reference list options
        builder.addCase(getProductCrossReferenceListOptions.pending, (state) => {
            state.loading = true;
        }).addCase(getProductCrossReferenceListOptions.fulfilled, (state, action) => {
            state.loading = false;
            state.productCrossReferenceListOptions = action.payload;
        }).addCase(getProductCrossReferenceListOptions.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });
    },
});

export const {
    resetCrossReferenceState,
    resetCrossReferenceStateSuccess,
    resetCrossReferenceStateError,
} = createCrossReferenceSlice.actions;
export default createCrossReferenceSlice.reducer;
